import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import { getCrmBrandTradeLicenseMappingsEndpointIdentifier } from './use-get-crm-brand-trade-license-mappings-api';

interface ToggleVerificationVariables {
  id: number;
  isVerified: boolean;
}

interface ToggleVerificationResponse {
  id: number;
  isVerified: boolean;
  updatedAt: string;
}

export function useToggleVerificationApi(options?: any) {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ id, isVerified }: ToggleVerificationVariables): Promise<ToggleVerificationResponse> => {
    try {
      const response = await http.patch(`ums/v1/crm/management/brands/trade-license/mappings/${id}/verification`, {
        isVerified,
      });
      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        title: 'Error Updating Verification',
        description: error.response.data.message || 'Failed to update verification status. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      throw error;
    }
  };

  return useMutationWrapper<ToggleVerificationResponse, AxiosError, ToggleVerificationVariables>(mutationFn, {
    onSuccess: () => {
      // Invalidate all mapping queries to force refetch
      queryClient.invalidateQueries([getCrmBrandTradeLicenseMappingsEndpointIdentifier]);
    },
    onError: () => {
      toast({
        title: 'Verification Update Failed',
        description: 'Something went wrong while updating verification status. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    ...options,
  });
}
